'use client';

import React, { useState, useMemo } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronLeft, ChevronRight, Check, Package, Square } from 'lucide-react';
import { DisplayProduct, ComboSelection } from '../types';
import Image from 'next/image';
import { Card, CardContent } from "@/components/ui/card";

interface ComboSelectionSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  combo: DisplayProduct | null;
  onComboSelected: (combo: DisplayProduct, selections: ComboSelection[]) => void;
}

export function ComboSelectionSheet({
  isOpen,
  onOpenChange,
  combo,
  onComboSelected
}: ComboSelectionSheetProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState<Record<string, string>>({});

  // Sort combo groups by display_order
  const sortedGroups = useMemo(() => {
    if (!combo?.combo_groups) return [];
    return [...combo.combo_groups].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  }, [combo?.combo_groups]);

  const currentGroup = sortedGroups[currentStep];
  const isLastStep = currentStep === sortedGroups.length - 1;
  const canProceed = currentGroup ? (
    currentGroup.is_optional || selections[currentGroup.id]
  ) : false;

  // Auto-select single required products
  React.useEffect(() => {
    if (currentGroup && !currentGroup.is_optional && currentGroup.combo_group_products?.length === 1) {
      const singleProduct = currentGroup.combo_group_products[0];
      if (singleProduct?.product_id && !selections[currentGroup.id]) {
        setSelections(prev => ({
          ...prev,
          [currentGroup.id]: singleProduct.product_id
        }));
      }
    }
  }, [currentGroup, selections]);

  const handleProductSelect = (groupId: string, productId: string) => {
    if (currentGroup?.group_type === 'selection') {
      // For selection type, only one product can be selected
      setSelections(prev => ({
        ...prev,
        [groupId]: productId
      }));
    } else {
      // For individual type, toggle selection (multiple allowed)
      setSelections(prev => {
        const current = prev[groupId] || '';
        const currentIds = current ? current.split(',') : [];
        
        if (currentIds.includes(productId)) {
          // Remove product
          const updated = currentIds.filter(id => id !== productId);
          return {
            ...prev,
            [groupId]: updated.join(',')
          };
        } else {
          // Add product
          return {
            ...prev,
            [groupId]: [...currentIds, productId].join(',')
          };
        }
      });
    }
  };

  const handleNext = () => {
    if (isLastStep) {
      // Complete selection
      if (combo) {
        const comboSelections: ComboSelection[] = [];
        
        Object.entries(selections).forEach(([groupId, productIds]) => {
          if (!productIds) return;
          
          const group = sortedGroups.find(g => g.id === groupId);
          if (!group) return;
          
          const productIdList = productIds.split(',').filter(Boolean);
          
          productIdList.forEach(productId => {
            const groupProduct = group.combo_group_products?.find(cgp => cgp.product_id === productId);
            if (groupProduct?.vendor_products) {
              comboSelections.push({
                group_id: groupId,
                group_name: group.name,
                selected_product_id: productId,
                selected_product_name: groupProduct.vendor_products.name,
                selected_product_image: groupProduct.vendor_products.image_url,
                is_vegetarian: groupProduct.vendor_products.is_vegetarian,
                contains_egg: groupProduct.vendor_products.contains_egg
              });
            }
          });
        });
        
        onComboSelected(combo, comboSelections);
      }
      handleClose();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  const handleClose = () => {
    setCurrentStep(0);
    setSelections({});
    onOpenChange(false);
  };

  const isProductSelected = (groupId: string, productId: string) => {
    const selected = selections[groupId] || '';
    return selected.split(',').includes(productId);
  };

  if (!combo) return null;

  return (
    <TooltipProvider>
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-[90vh] bg-muted p-0 flex flex-col">
        <SheetHeader className="flex-shrink-0 bg-white px-2 py-4">
          <div className="flex items-center gap-2 mb-2">
            <Package className="h-5 w-5 text-blue-600" />
            <SheetTitle>{combo.name}</SheetTitle>
          </div>
        </SheetHeader>

        {/* Current Step Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {currentGroup && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{currentGroup.name}</h3>
                <div className="flex gap-2">
                  {currentGroup.is_optional && (
                    <Badge variant="outline">Optional</Badge>
                  )}
                  <div className="flex gap-1 text-xs text-muted-foreground">
                    {currentGroup.group_type === 'selection' ? 'Select any 1' : 'Select any'}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {currentGroup.combo_group_products?.map((cgp) => {
                  if (!cgp.vendor_products) return null;
                  
                  const isSelected = isProductSelected(currentGroup.id, cgp.product_id);
                  const isAutoSelected = !currentGroup.is_optional && 
                    currentGroup.combo_group_products?.length === 1 && 
                    isSelected;
                  
                  return (
                    <Card
                      key={cgp.id}
                      className={`transition-all duration-200 ${
                        isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:shadow-md'
                      } ${isAutoSelected ? 'cursor-default' : 'cursor-pointer'}`}
                      onClick={() => !isAutoSelected && handleProductSelect(currentGroup.id, cgp.product_id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          {cgp.vendor_products.image_url && (
                            <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                              <Image
                                src={cgp.vendor_products.image_url}
                                alt={cgp.vendor_products.name}
                                fill
                                className="object-cover"
                              />
                            </div>
                          )}
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-gray-900">{cgp.vendor_products.name}</h4>
                              <div className="flex gap-1">
                                {cgp.vendor_products.is_vegetarian === true ? (
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <div className="relative h-4 w-4">
                                        <Square className="h-4 w-4 text-green-600 fill-white stroke-2" />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                          <div className="h-1.5 w-1.5 bg-green-600 rounded-full"></div>
                                        </div>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>Vegetarian</TooltipContent>
                                  </Tooltip>
                                ) : cgp.vendor_products.is_vegetarian === false ? (
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <div className="relative h-4 w-4">
                                        <Square className="h-4 w-4 text-red-600 fill-white stroke-2" />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                          <div className="h-1.5 w-1.5 bg-red-600 rounded-full"></div>
                                        </div>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>Non-vegetarian</TooltipContent>
                                  </Tooltip>
                                ) : null}
                                {cgp.vendor_products.contains_egg === true && (
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <div className="relative h-4 w-4">
                                        <Square className="h-4 w-4 text-amber-500 fill-white stroke-2" />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                          <div className="h-1.5 w-1.5 bg-amber-500 rounded-full"></div>
                                        </div>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>Contains Egg</TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                            {cgp.vendor_products.description && (
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                {cgp.vendor_products.description}
                              </p>
                            )}
                          </div>
                          
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                            isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Navigation Footer */}
        <div className="flex-shrink-0 border-t p-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            
            <div className="text-sm text-gray-500">
              Step {currentStep + 1} of {sortedGroups.length}
            </div>
            
            <Button
              onClick={handleNext}
              disabled={!canProceed}
              className="min-w-[100px]"
            >
              {isLastStep ? 'Add to Cart' : 'Next'}
              {!isLastStep && <ChevronRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
    </TooltipProvider>
  );
}